import { useTranslation } from 'react-i18next'
import type { CamionMuelle, CargaSalida } from '@/types'
import { COLOR_ESTADO, estadoCamionDe, hhmm, paletsDeCamion } from '@/utils/camionesMuelle'
import { pickingPct } from '@/utils/cargasSalida'

/**
 * Un camión dentro del carril de su muelle, pintado como un **evento de
 * calendario**: el color del estado a sangre y el texto encima.
 *
 * Es el lenguaje que la gente ya sabe leer en una rejilla de horas —Outlook,
 * Google Calendar— y, a diferencia de una tarjeta clara, se ve a cualquier
 * ancho: en la vista semanal un camión de noventa minutos ocupa el 1,5 % del
 * carril, y ahí un borde de color no se distingue pero un bloque sí.
 *
 * Dentro, la hora manda. Es el dato por el que se mira este cuadro, así que va
 * primero y en peso.
 *
 * El picking es una banda MÁS OSCURA del mismo color, no otro color: dice cuánto
 * se ha preparado sin introducir una segunda escala cromática que compita con la
 * del estado.
 *
 * No recibe `dark`: una barra de color pleno se lee igual en los dos temas, y
 * teñirla distinta rompería la correspondencia con la leyenda y los filtros.
 */
export function BarraCamion({
  camion, cargas, left, width, top, alto, minLabelPct, onClick, arrastrando,
}: {
  camion: CamionMuelle
  /** Las cargas enganchadas, ya resueltas. Puede ir vacía: un camión se reserva
   *  antes de saber qué se le mete. */
  cargas: CargaSalida[]
  left: number
  width: number
  top: number
  alto: number
  minLabelPct: number
  onClick?: () => void
  /** Hay una carga sobrevolando el carril: la barra se apaga para que se vea el
   *  hueco donde se va a soltar. */
  arrastrando?: boolean
}) {
  const { t } = useTranslation()
  const estado = estadoCamionDe(camion, cargas)
  const color = COLOR_ESTADO[estado]
  const palets = paletsDeCamion(camion, cargas)

  const rango = `${hhmm(camion.fechaInicio)}–${hhmm(camion.fechaFin)}`
  const avance = cargas.length
    ? Math.round(cargas.reduce((a, c) => a + pickingPct(c), 0) / cargas.length)
    : null

  const detalle = [
    camion.codigo,
    camion.muelle,
    rango,
    camion.transportista,
    camion.matricula,
    palets.total !== null
      ? t(palets.manual ? 'muelles.palets' : 'muelles.paletsEstimados', { n: palets.total })
      : t('muelles.sinPalets'),
    cargas.length ? t('muelles.nCargas', { count: cargas.length }) : t('muelles.sinCargas'),
    avance !== null ? t('muelles.chips.pickingTooltip', { pct: avance }) : undefined,
    t(`muelles.estado.${estado}`),
  ].filter(Boolean).join(' · ')

  const cabeTexto = width > minLabelPct
  const cabeSegundaLinea = alto >= 38 && width > minLabelPct * 2
  // Un camión anulado no compite con los vivos: se queda a media tinta.
  const apagado = estado === 'anulado' || estado === 'salido'

  return (
    <button
      type="button"
      onClick={onClick}
      title={detalle}
      className={`group absolute rounded-md overflow-hidden text-left text-white
        ${color.solido}
        ${apagado ? 'opacity-70' : ''}
        ${arrastrando ? 'opacity-30' : 'hover:brightness-110 hover:-translate-y-px'}
        shadow-sm transition-[opacity,filter,transform,box-shadow] duration-150
        hover:shadow-md focus-visible:outline focus-visible:outline-2
        focus-visible:outline-offset-1 focus-visible:outline-white/70`}
      style={{
        left: `calc(${left}% + 2px)`,
        width: `calc(${width}% - 4px)`,
        top,
        height: alto,
        minWidth: 6,
      }}
    >
      {/* El picking, como base más oscura del mismo color. */}
      {avance !== null && avance > 0 && (
        <span
          className={`absolute bottom-0 left-0 h-[3px] ${color.avance}`}
          style={{ width: `${avance}%` }}
          aria-hidden
        />
      )}

      {cabeTexto && (
        <div className="absolute inset-0 flex flex-col justify-center gap-0.5 px-2 overflow-hidden">
          <div className="flex items-baseline gap-1.5 overflow-hidden">
            <span className="text-[11px] font-bold tabular-nums shrink-0">
              {hhmm(camion.fechaInicio)}
            </span>
            <span className="text-[10px] font-medium text-white/80 truncate">
              {camion.codigo ?? '—'}
            </span>
            {estado === 'enRiesgo' && (
              <span className="ml-auto text-[10px] font-bold shrink-0" aria-hidden>▲</span>
            )}
          </div>

          {cabeSegundaLinea && (
            <div className="flex items-center gap-1.5 text-[10px] tabular-nums text-white/85 truncate">
              {cargas.length > 0 && <span>{t('muelles.nCargas', { count: cargas.length })}</span>}
              {palets.total !== null && (
                <span>{palets.manual ? '' : '≈'}{palets.total} pal</span>
              )}
              {camion.transportista && <span className="truncate">{camion.transportista}</span>}
            </div>
          )}
        </div>
      )}
    </button>
  )
}
